import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api/apiClient';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterService, setFilterService] = useState([]);
  const [filterShop, setFilterShop] = useState([]);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [shopsDropdownOpen, setShopsDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Fetch appointments from API
  const fetchAppointments = async () => {
    try {
      const client = getApiClient();
      const response = await client.get('/admin/onlineAppoints');

      setAppointments(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to fetch appointments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAppointments();
  }, []);

  // Helper function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getShops = () => {
    const shops = [...new Set(appointments.map(app => app.shopName).filter(Boolean))];
    return shops;
  };
  // Helper function to format time
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Helper function to get unique specializations
  const getSpecializations = () => {
    const specializations = [...new Set(appointments.map(app => app.appointmentData?.specialization).filter(Boolean))];
    return specializations;
  };

  // Helper function to handle service filter changes
  const handleServiceFilterChange = (service) => {
    setFilterService(prev => {
      if (prev.includes(service)) {
        return prev.filter(s => s !== service);
      } else {
        return [...prev, service];
      }
    });
  };

  // Helper function to select all services
  const selectAllServices = () => {
    setFilterService(getSpecializations());
  };

  // Helper function to clear all services
  const clearAllServices = () => {
    setFilterService([]);
  };

  // Helper function to handle shop filter changes
  const handleShopFilterChange = (shop) => {
    setFilterShop(prev => {
      if (prev.includes(shop)) {
        return prev.filter(s => s !== shop);
      } else {
        return [...prev, shop];
      }
    });
  };

  // Helper function to select all shops
  const selectAllShops = () => {
    setFilterShop(getShops());
  };

  // Helper function to clear all shops
  const clearAllShops = () => {
    setFilterShop([]);
  };

  // Helper function to handle status filter changes
  const handleStatusFilterChange = (status) => {
    setFilterStatus(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Helper function to select all statuses
  const selectAllStatuses = () => {
    setFilterStatus(['confirmed', 'pending']);
  };

  // Helper function to clear all statuses
  const clearAllStatuses = () => {
    setFilterStatus([]);
  };

  // Filter appointments based on current filters
  const filteredAppointments = appointments
    .filter(appointment => {
      const appointmentDate = appointment.appointmentData?.appointmentStart ? 
        new Date(appointment.appointmentData.appointmentStart).toISOString().split('T')[0] : '';
      
      const matchesDate = !filterDate || appointmentDate === filterDate;
      const matchesStatus = filterStatus.length === 0 || 
        (filterStatus.includes('confirmed') && appointment.confirmed) ||
        (filterStatus.includes('pending') && !appointment.confirmed);
      const matchesService = filterService.length === 0 || 
        filterService.includes(appointment.appointmentData?.specialization);
      const matchesShop = filterShop.length === 0 || 
        filterShop.includes(appointment.shopName);
      
      return matchesDate && matchesStatus && matchesService && matchesShop;
    })
    .sort((a, b) => {
      // Sort by appointment date/time in descending order (newest first)
      const dateA = a.appointmentData?.appointmentStart ? new Date(a.appointmentData.appointmentStart) : new Date(0);
      const dateB = b.appointmentData?.appointmentStart ? new Date(b.appointmentData.appointmentStart) : new Date(0);
      return dateB - dateA;
    });

  // Get status badge
  const getStatusBadge = (confirmed) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        confirmed 
          ? 'bg-green-100 text-green-800' 
          : 'bg-yellow-100 text-yellow-800'
      }`}>
        {confirmed ? 'Confirmed' : 'Pending'}
      </span>
    );
  };

  // Get today's appointments
  const getTodayAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(app => {
      const appointmentDate = app.appointmentData?.appointmentStart ? 
        new Date(app.appointmentData.appointmentStart).toISOString().split('T')[0] : '';
      return appointmentDate === today;
    });
  };

  // Get upcoming appointments
  const getUpcomingAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(app => {
      const appointmentDate = app.appointmentData?.appointmentStart ? 
        new Date(app.appointmentData.appointmentStart).toISOString().split('T')[0] : '';
      return appointmentDate > today;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-gray-600">Loading appointments...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {/* <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Online Appointments</h1>
            <p className="mt-2 text-lg text-gray-600">
              Manage customer appointments from your online booking system
            </p>
          </div>
          <button
            onClick={fetchAppointments}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {refreshing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Refreshing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </>
            )}
          </button>
        </div> */}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards - horizontally scrollable on mobile */}
        <div className="mb-8 overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max md:grid md:grid-cols-2 lg:grid-cols-4 md:min-w-0">
          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Today's Appointments</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{getTodayAppointments().length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Upcoming</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{getUpcomingAppointments().length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Confirmed</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">
                      {appointments.filter(app => app.confirmed).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg w-48 flex-shrink-0 md:w-auto">
            <div className="p-4 md:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4 md:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs md:text-sm font-medium text-gray-500 truncate">Total</dt>
                    <dd className="text-base md:text-lg font-medium text-gray-900">{appointments.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <span className="block truncate">
                    {filterStatus.length === 0 
                      ? 'All Statuses' 
                      : `${filterStatus.length} selected`}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
                
                {statusDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={selectAllStatuses}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={clearAllStatuses}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes('confirmed')}
                        onChange={() => handleStatusFilterChange('confirmed')}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mr-3"
                      />
                      <span className="text-sm text-gray-700">Confirmed</span>
                    </label>
                    <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes('pending')}
                        onChange={() => handleStatusFilterChange('pending')}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mr-3"
                      />
                      <span className="text-sm text-gray-700">Pending</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Services</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                  className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <span className="block truncate">
                    {filterService.length === 0 
                      ? 'All Services' 
                      : `${filterService.length} selected`}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
                
                {servicesDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={selectAllServices}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={clearAllServices}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    {getSpecializations().map(service => (
                      <label key={service} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterService.includes(service)}
                          onChange={() => handleServiceFilterChange(service)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mr-3"
                        />
                        <span className="text-sm text-gray-700">{service}</span>
                      </label>
                    ))}
                    {getSpecializations().length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-500">No services available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Shops</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShopsDropdownOpen(!shopsDropdownOpen)}
                  className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <span className="block truncate">
                    {filterShop.length === 0 
                      ? 'All Shops' 
                      : `${filterShop.length} selected`}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
                
                {shopsDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <div className="flex justify-between">
                        <button
                          type="button"
                          onClick={selectAllShops}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={clearAllShops}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    {getShops().map(shop => (
                      <label key={shop} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filterShop.includes(shop)}
                          onChange={() => handleShopFilterChange(shop)}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded mr-3"
                        />
                        <span className="text-sm text-gray-700">{shop}</span>
                      </label>
                    ))}
                    {getShops().length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-500">No shops available</p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterDate('');
                  setFilterStatus([]);
                  setFilterService([]);
                  setFilterShop([]);
                  setStatusDropdownOpen(false);
                  setServicesDropdownOpen(false);
                  setShopsDropdownOpen(false);
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition duration-150 ease-in-out"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Appointments Table (hidden on mobile) */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg hidden md:block">
          <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Online Appointments</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Recent appointments booked through your online system
            </p>
          </div>

          {filteredAppointments.length === 0 ? (
            <div className="p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No appointments</h3>
              <p className="mt-1 text-sm text-gray-500">No appointments found matching your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Service
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shop
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAppointments.map((appointment, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {appointment.appointmentData?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.appointmentData?.email || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {appointment.appointmentData?.phone || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.appointmentData?.carMake && appointment.appointmentData?.carType
                            ? `${appointment.appointmentData.carMake} ${appointment.appointmentData.carType}`
                            : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {appointment.appointmentData?.registrationNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.appointmentData?.specialization || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.appointmentData?.appointmentStart 
                            ? formatDate(appointment.appointmentData.appointmentStart)
                            : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {appointment.appointmentData?.appointmentStart 
                            ? formatTime(appointment.appointmentData.appointmentStart)
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {appointment.shopName || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {appointment.appointmentData?.shopId 
                            ? `ID: ${appointment.appointmentData.shopId.substring(0, 8)}...`
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(appointment.confirmed)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Card Layout (only on mobile) */}
        <div className="md:hidden">
          {filteredAppointments.length === 0 ? (
            <div className="p-6 text-center bg-white rounded-lg shadow">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No appointments</h3>
              <p className="mt-1 text-sm text-gray-500">No appointments found matching your filters.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredAppointments.map((appointment, index) => (
                <div key={index} className="bg-white rounded-lg shadow p-4 flex flex-col space-y-2">
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Customer</span>
                    <div className="text-sm font-medium text-gray-900">{appointment.appointmentData?.name || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{appointment.appointmentData?.email || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{appointment.appointmentData?.phone || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Vehicle</span>
                    <div className="text-sm text-gray-900">
                      {appointment.appointmentData?.carMake && appointment.appointmentData?.carType
                        ? `${appointment.appointmentData.carMake} ${appointment.appointmentData.carType}`
                        : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">{appointment.appointmentData?.registrationNumber || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Service</span>
                    <div className="text-sm text-gray-900">{appointment.appointmentData?.specialization || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Date & Time</span>
                    <div className="text-sm text-gray-900">
                      {appointment.appointmentData?.appointmentStart 
                        ? formatDate(appointment.appointmentData.appointmentStart)
                        : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {appointment.appointmentData?.appointmentStart 
                        ? formatTime(appointment.appointmentData.appointmentStart)
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Shop</span>
                    <div className="text-sm text-gray-900">{appointment.shopName || 'N/A'}</div>
                    <div className="text-sm text-gray-500">
                      {appointment.appointmentData?.shopId 
                        ? `ID: ${appointment.appointmentData.shopId.substring(0, 8)}...`
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-gray-500">Status</span>
                    <div>{getStatusBadge(appointment.confirmed)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Information Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About Online Appointments</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  This page displays all appointments booked through your online booking system. 
                  You can filter by date, status, and service type to find specific appointments. 
                  The data is automatically refreshed from your booking platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Appointments;
